export class WorkspaceBootstrapError extends Error {
  constructor(message = 'We could not finish setting up your workspace. Please try again.') {
    super(message)
    this.name = 'WorkspaceBootstrapError'
  }
}

function safeUserName(email, getUserFromToken) {
  const tokenUser = getUserFromToken?.()
  return tokenUser?.name || String(email || '').split('@')[0] || 'WorkflowPro user'
}

function getSafeCompanyName(companyName, email, getUserFromToken) {
  const trimmed = String(companyName || '').trim()
  return trimmed || `${safeUserName(email, getUserFromToken)}'s Workspace`
}

async function getOrCreateWorkspace({ email, name, companyName }, services) {
  try {
    const existing = await services.authApi.getOrg()
    if (existing?.org?.orgId) {
      return { org: existing.org, created: false }
    }
  } catch {
    // Missing workspace is repaired below. Other failures surface if creation also fails.
  }

  try {
    const created = await services.authApi.createOrg({
      companyName: getSafeCompanyName(companyName, email, services.getUserFromToken),
      email,
      name: String(name || '').trim() || safeUserName(email, services.getUserFromToken),
    })

    if (!created?.orgId && !created?.org?.orgId) {
      throw new WorkspaceBootstrapError()
    }

    return { org: created.org || { orgId: created.orgId }, created: true }
  } catch {
    throw new WorkspaceBootstrapError()
  }
}

async function acceptPendingInviteOnce(services) {
  const pendingInvite = services.storage.getItem('pending_invite')
  if (!pendingInvite) return { accepted: false }

  try {
    const { token, inviteId, orgId } = JSON.parse(pendingInvite)
    if (!token || !inviteId || !orgId) {
      services.storage.removeItem('pending_invite')
      return { accepted: false }
    }

    await services.teamApi.acceptInvite(token, inviteId, orgId)
    services.storage.removeItem('pending_invite')
    return { accepted: true, destination: '/dashboard' }
  } catch {
    throw new WorkspaceBootstrapError('We signed you in, but could not finish joining the invited workspace. Please try again.')
  }
}

async function claimAnonymousSessionOnce(services) {
  const anonSession = services.getAnonSession()
  if (!anonSession?.sessionId) return { claimed: false }

  try {
    const claimResult = await services.anonApi.claimSession(anonSession)
    if (claimResult?.success === false) {
      return { claimed: false, warning: 'Your anonymous results could not be transferred yet.' }
    }

    services.clearAnonSession()
    return {
      claimed: true,
      destination: claimResult?.jobId ? `/dashboard/jobs/${claimResult.jobId}` : '/dashboard',
    }
  } catch (error) {
    if (error?.status === 410 || error?.payload?.expired) {
      services.clearAnonSession()
      return { claimed: false, warning: 'Your anonymous results expired before they could be transferred.' }
    }

    return { claimed: false, warning: 'Your anonymous results could not be transferred yet.' }
  }
}

export function createBootstrapAuthenticatedUser(defaultServices) {
  let workspaceBootstrapPromise = null

  return async function bootstrapAuthenticatedUser(options = {}, injectedServices = {}) {
    if (workspaceBootstrapPromise) return workspaceBootstrapPromise

    const services = {
      ...defaultServices,
      ...injectedServices,
    }

    workspaceBootstrapPromise = (async () => {
      const requestedDestination = options.destination || '/dashboard'
      const invite = await acceptPendingInviteOnce(services)
      const workspace = invite.accepted
        ? { org: null, created: false }
        : await getOrCreateWorkspace(options, services)

      const claim = await claimAnonymousSessionOnce(services)

      return {
        org: workspace.org,
        workspaceCreated: workspace.created,
        inviteAccepted: invite.accepted,
        anonymousClaimed: claim.claimed,
        warning: claim.warning || '',
        destination: claim.destination || invite.destination || requestedDestination,
      }
    })()

    try {
      return await workspaceBootstrapPromise
    } finally {
      workspaceBootstrapPromise = null
    }
  }
}
